import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { User, Building2 } from 'lucide-react';

export default function Signup() {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [userType, setUserType] = useState('PERSONAL');
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const user = await signup(email, password, name, userType);
      toast.success(t('success.saved'));
      
      if (user.userType === 'PERSONAL') {
        navigate('/personal/dashboard');
      } else {
        navigate('/enterprise/orgs');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <h1 className="font-heading text-4xl font-bold text-gray-900">{t('auth.signupTitle')}</h1>
            <p className="mt-2 text-gray-600">{t('auth.signupSubtitle')}</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 space-y-6">
            <div className="flex gap-2 p-1 bg-gray-100 rounded-xl">
              <button
                data-testid="personal-signup-tab"
                onClick={() => setUserType('PERSONAL')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg font-medium transition-all text-sm ${
                  userType === 'PERSONAL'
                    ? 'bg-white shadow-sm text-primary'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <User className="w-4 h-4" />
                {t('auth.individual')}
              </button>
              <button
                data-testid="enterprise-signup-tab"
                onClick={() => setUserType('ENTERPRISE')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg font-medium transition-all text-sm ${
                  userType === 'ENTERPRISE'
                    ? 'bg-white shadow-sm text-enterprise-slate'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Building2 className="w-4 h-4" />
                {t('auth.enterprise')}
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">{t('auth.name')}</Label>
                <Input
                  id="name"
                  data-testid="signup-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('auth.namePlaceholder')}
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input
                  id="email"
                      data-testid="signup-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('auth.emailPlaceholder')}
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="password">{t('auth.password')}</Label>
                    <Input
                      id="password"
                      data-testid="signup-password-input"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('auth.passwordPlaceholder')}
                      required
                      minLength={8}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">{t('auth.passwordMinLength')}</p>
                  </div>

                  <Button
                    data-testid="signup-submit-button"
                    type="submit"
                    className="w-full"
                    disabled={loading}
                  >
                    {loading ? t('common.loading') : t('auth.signup')}
                  </Button>
                </form>

                <div className="text-center space-y-2">
                  <Link
                    to="/login"
                    className="text-sm text-primary hover:text-primary-hover"
                    data-testid="login-link"
                  >
                    {t('auth.hasAccount')} {t('auth.loginLink')}
                  </Link>
                </div>
          </div>
        </div>
      </div>

      <div 
        className={`hidden lg:flex flex-1 items-center justify-center p-12 ${
          userType === 'PERSONAL' 
            ? 'bg-gradient-to-br from-primary/10 to-personal-warm/10'
            : 'bg-gradient-to-br from-enterprise-slate/10 to-enterprise-steel/10'
        }`}
      >
        <div className="max-w-md text-center space-y-6">
          {userType === 'PERSONAL' ? (
            <>
              <h2 className="font-heading text-3xl font-bold text-gray-900">
                {t('auth.personalSignupTitle')}
              </h2>
              <p className="text-lg text-gray-600">
                {t('auth.personalSignupDesc')}
              </p>
            </>
          ) : (
            <>
              <h2 className="font-heading text-3xl font-bold text-gray-900">
                {t('auth.enterpriseSignupTitle')}
              </h2>
              <p className="text-lg text-gray-600">
                {t('auth.enterpriseSignupDesc')}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
